import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ApartmentIcon from '@mui/icons-material/Apartment';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { useDispatch, useSelector } from 'react-redux';
import {
  configIntegration,
  getCommunities,
  getTenantDetails,
  updateCommunity
} from '../../../Redux/Actions/SelfServe';
import {
  addSelectedApartment,
  removeSelectedApartment
} from '../../../Redux/Reducer/SelfServe';
import Grid from './Grid';
import OnlivePopUp from './onLiveModal';
import SettingsModal from './SettingsModal';
import ReadinessCheckDialog from '../Communities/ReadinessCheckDialog';

interface Community {
  _id: string;
  name: string;
  addressId?: { fullAdd?: string };
  communityPhone?: string;
  preLiveLeadsIngested?: number;
  integrations?: boolean;
  isLive?: boolean;
  leadIngestion?: boolean;
  isSettingsEnable?: boolean;
  knockWebhookEnabled?: boolean | null;
  isCLOProperty?: boolean;
}

interface Notification {
  success: boolean;
  msg: string;
}

interface IntegrationsProps {
  adminTenantId: string;
}

const Integrations: React.FC<IntegrationsProps> = ({
  adminTenantId: tenantId
}) => {
  const selfServe = useSelector((state: any) => state.SelfServe);
  const { integrationApartment, selectedApartment } = selfServe;
  const notLiveApartments: Community[] | undefined =
    integrationApartment?.filter(
      (item: Community) => !item.isLive || !item?.leadIngestion
    );
  const liveApartments: Community[] | undefined = integrationApartment?.filter(
    (item: Community) => item.isLive && item?.leadIngestion
  );
  const settingsEnabled: boolean =
    selfServe?.userData?.data?.crmSetup ||
    selfServe?.userData?.data?.pmsSetup ||
    false;
  const tenantData = selfServe?.userData?.data || {};
  const crmSetup: boolean = selfServe?.userData?.data?.crmSetup || false;
  const pmsSetup: boolean =
    selfServe?.userData?.data?.pmsSetup ||
    selfServe?.userData?.data?.propertyIngestion ||
    false;
  const initalStatusMapping: any[] =
    selfServe?.userData?.data?.statusMappings?.mappings || [];
  const statusMappings = selfServe?.userData?.data?.statusMappings || {};
  const settingsName: string = selfServe?.userData?.data?.settingsName || '';
  const assignToKelsey: boolean =
    selfServe?.userData?.data?.assignLeadToKelsey || false;
  const dispatch = useDispatch<any>();
  const [open, setOpenPopup] = useState(false);
  const [appliedSettings, setAppliedSettings] = useState<string[]>([]);
  const [onLiveSettings, setOnLiveSettings] = useState<string[]>([]);
  const [notification, setNotification] = useState<Notification>({
    success: false,
    msg: ''
  });
  const [showUpdatedApartment, setShowUpdatedApartment] = useState<Community[]>(
    []
  );
  const [readinessCheckOpen, setReadinessCheckOpen] = useState(false);

  const closePop = () => {
    setOnLiveSettings([]);
    setOpenPopup(false);
  };

  const [isSettingsModal, setIsSettingsModal] = useState(false);
  const [searchVal, setSearchVal] = useState('');

  const handleSettingsClose = () => {
    setIsSettingsModal(false);
    if (tenantId) {
      dispatch(getTenantDetails(tenantId));
    }
  };

  const handleSettingsOpen = () => {
    if (!settingsEnabled) {
      setIsSettingsModal(true);
    } else {
      setOnLiveSettings([]);
      setOpenPopup(true);
    }
  };

  const editSettings = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (tenantId) {
      dispatch(getTenantDetails(tenantId));
    }
    setIsSettingsModal(true);
  };

  const removeOnLive = (data: string) => {
    setOnLiveSettings(prev => prev.filter(item => item !== data));
  };

  const addOnLiveData = (data: string) => {
    setOnLiveSettings(prev => [...prev, data]);
  };

  const applySettingsData = (data: string) => {
    setAppliedSettings(prev => {
      const filter = integrationApartment.findIndex(
        (item: Community) => item?._id === data
      );
      dispatch(addSelectedApartment(integrationApartment[filter]));
      return [...prev, data];
    });
  };

  const removeOnboard = (data: string) => {
    setAppliedSettings(prev => {
      const updatedData = prev.filter(item => item !== data);
      const filter = selectedApartment?.filter(
        (item: Community) => item?._id !== data
      );
      dispatch(removeSelectedApartment(filter));
      return updatedData;
    });
  };

  const filteredApartment = (
    searchVal?.length ? showUpdatedApartment : notLiveApartments
  )
    ?.slice()
    .sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));

  const handleChangeSearch = (value: string) => {
    setSearchVal(value);
    setShowUpdatedApartment(() => {
      const searchTerm = value.toLowerCase();
      const filtered = notLiveApartments?.filter((item: Community) =>
        item?.name?.toLowerCase().startsWith(searchTerm)
      );
      return filtered || [];
    });
  };

  const handleApplySettings = async () => {
    if (!appliedSettings?.length) {
      setNotification({
        msg: 'Please select a community first',
        success: false
      });
      return;
    }
    const launchCount = appliedSettings.length;
    try {
      await Promise.all(
        appliedSettings.map(communityId =>
          dispatch(
            updateCommunity({
              payload: {
                community_id: communityId,
                client_id: tenantId,
                is_live: true,
                lead_ingestion: true,
                is_settings_enable: true,
                sync_property: true
              }
            })
          ).unwrap()
        )
      );
      setNotification({
        msg: `${launchCount} ${
          launchCount === 1 ? 'community' : 'communities'
        } launched successfully`,
        success: true
      });
      setAppliedSettings([]);
      dispatch(getCommunities({ tenantId }));
    } catch {
      setNotification({
        msg: 'Launch failed, please try again',
        success: false
      });
    }
  };

  const handleRemoveLive = async () => {
    if (!onLiveSettings?.length) {
      setNotification({
        msg: 'Please select a community first',
        success: false
      });
      return;
    }
    const removeCount = onLiveSettings.length;
    try {
      await Promise.all(
        onLiveSettings.map(communityId =>
          dispatch(
            updateCommunity({
              payload: {
                community_id: communityId,
                client_id: tenantId,
                integrations: false,
                is_live: false,
                lead_ingestion: false,
                is_settings_enable: false,
                sync_property: true
              }
            })
          ).unwrap()
        )
      );
      setNotification({
        msg: `${removeCount} ${
          removeCount === 1 ? 'community' : 'communities'
        } removed from live`,
        success: true
      });
      setOnLiveSettings([]);
      dispatch(getCommunities({ tenantId }));
    } catch {
      setNotification({
        msg: 'Removal failed, please try again',
        success: false
      });
    }
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, msg: '' }));
  };

  const handleRefresh = () => {
    dispatch(getCommunities({ tenantId }));
  };

  useEffect(() => {
    if (tenantId) {
      dispatch(getTenantDetails(tenantId));
      dispatch(getCommunities({ tenantId }));
    }
  }, [tenantId]);

  const count =
    liveApartments?.filter(
      (item: Community) =>
        item?.integrations && (item?.leadIngestion || item?.isLive)
    ).length || 0;

  return (
    <Paper sx={{ bgcolor: 'background.paper', py: '20px' }} elevation={0}>
      {/* Top Bar: Search + Actions */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: '20px',
          mb: '16px'
        }}
      >
        <TextField
          onChange={e => handleChangeSearch(e.target.value)}
          placeholder="Search communities"
          variant="outlined"
          size="small"
          value={searchVal}
          sx={{ width: '300px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            )
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <IconButton onClick={handleRefresh} size="small">
            <RefreshIcon sx={{ color: 'text.secondary' }} />
          </IconButton>

          <Button
            variant="outlined"
            onClick={() => setReadinessCheckOpen(true)}
            disabled={appliedSettings.length === 0}
            startIcon={<FactCheckOutlinedIcon />}
          >
            Run Checks
          </Button>
          <Button
            variant="contained"
            onClick={handleApplySettings}
            disabled={!settingsEnabled || appliedSettings.length === 0}
            startIcon={<RocketLaunchIcon />}
          >
            Launch
          </Button>
        </Box>
      </Box>

      {/* Integration Grid */}
      <Box
        sx={{
          px: '20px',
          pb: '20px',
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '16px',
          maxHeight: '50vh',
          overflowY: 'auto'
        }}
      >
        {filteredApartment && filteredApartment.length ? (
          filteredApartment
            .filter(item => item?.integrations)
            .map(item => (
              <Grid
                tag={true}
                key={item?._id}
                apptId={item?._id}
                header={item.name}
                address={item.addressId}
                communityPhone={item.communityPhone}
                preLiveLeadsIngested={item.preLiveLeadsIngested || 0}
                knockWebhookEnabled={item.knockWebhookEnabled ?? null}
                applySettingsData={applySettingsData}
                removeOnboard={removeOnboard}
                error={false}
                isCLOProperty={item?.isCLOProperty}
              />
            ))
        ) : (
          <Typography sx={{ color: 'text.secondary', py: '40px' }}>
            No onboarded communities found.
          </Typography>
        )}
      </Box>

      {/* Live Communities Card */}
      <Box
        onClick={handleSettingsOpen}
        sx={{
          mx: '20px',
          my: '20px',
          p: '16px',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          '&:hover': { bgcolor: 'action.hover' }
        }}
      >
        {settingsEnabled ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              width: '100%'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flexGrow: 1
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Live Communities
              </Typography>
              <Chip
                icon={<ApartmentIcon sx={{ fontSize: '16px' }} />}
                label={count}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
              >
                <Typography
                  variant="body2"
                  sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <CheckCircleIcon
                    sx={{ fontSize: '16px', color: 'success.main' }}
                  />
                  Property ingestion (PMS)
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <CheckCircleIcon
                    sx={{ fontSize: '16px', color: 'success.main' }}
                  />
                  Lead ingestion (CRM)
                </Typography>
              </Box>
              <IconButton onClick={editSettings} size="small">
                <SettingsIcon />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <SettingsIcon sx={{ color: 'text.secondary' }} />
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, color: 'text.secondary' }}
            >
              Configure Integration
            </Typography>
          </Box>
        )}
      </Box>

      {isSettingsModal && (
        <SettingsModal
          statusMappings={statusMappings}
          tenantData={tenantData}
          tenantId={tenantId}
          configIntegration={configIntegration}
          handleClose={handleSettingsClose}
          settingsInitailValue={settingsName}
          assignToKelsey={assignToKelsey}
          crmSetupInitial={crmSetup}
          pmsSetupInitial={pmsSetup}
          initalStatusMapping={initalStatusMapping}
        />
      )}

      <ReadinessCheckDialog
        open={readinessCheckOpen}
        onClose={() => setReadinessCheckOpen(false)}
        onOnboard={() => {}}
        mode="check"
        communityIds={appliedSettings}
        communities={
          integrationApartment?.filter((c: Community) =>
            appliedSettings.includes(c._id)
          ) || []
        }
        tenantId={tenantId}
      />

      <OnlivePopUp
        removeOnLive={removeOnLive}
        addOnLiveData={addOnLiveData}
        handleRemoveLive={handleRemoveLive}
        apartmentData={liveApartments}
        open={open}
        closePop={closePop}
        onLiveSettings={onLiveSettings}
        tenantId={tenantId}
      />

      <Snackbar
        open={!!notification?.msg}
        autoHideDuration={4000}
        onClose={closeNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={closeNotification}
          severity={notification?.success ? 'success' : 'error'}
        >
          {notification?.msg}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default Integrations;
